import { SxProps, Theme, Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';

import { Autocomplete, AutocompleteOption, UNKNOWN_OPTION_VALUE } from '~/components/Autocomplete';
import { DateField } from '~/components/DateField';
import { TextField } from '~/components/TextField';
import { GetCompanyResponse } from '~/services/rtkQuery/company/types';

import { Round } from './components/Round';
import styles from './styles.module.scss';

const EMPTY_OPTIONS: AutocompleteOption[] = [];
const COMPANY_BIO_TEXT_FIELD_SX: SxProps<Theme> = {
  gridColumn: 'span 2',
  '& .MuiInputBase-input': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
};
const COMPETITORS_AUTOCOMPLETE_SX: SxProps<Theme> = { gridColumn: 'span 2' };

interface Props {
  data: GetCompanyResponse['result']['data'];
}

const Component: React.FC<Props> = ({ data }) => {
  const selectedIndustries = useMemo<AutocompleteOption[]>(() => {
    return data.industries.map(industry => ({ value: industry.id, label: industry.name }));
  }, [data.industries]);

  const selectedFirstFinancingDealType = useMemo<AutocompleteOption>(() => {
    return {
      value: data.first_financing_deal_type.id,
      label: data.first_financing_deal_type.name,
    };
  }, [data.first_financing_deal_type.id, data.first_financing_deal_type.name]);

  const firstFinancingDate = useMemo(() => {
    return new Date(data.first_financing_date);
  }, [data.first_financing_date]);

  const selectedCompetitors = useMemo<AutocompleteOption[]>(() => {
    return data.competitors
      ? data.competitors.map(competitor => ({
          value: competitor.external_id,
          label: competitor.name,
        }))
      : [{ label: 'Competitors unknown', value: UNKNOWN_OPTION_VALUE }];
  }, [data.competitors]);

  return (
    <div className={styles.container}>
      <div className={styles['container__general-info']}>
        <Typography color="text.primary" fontWeight="700" mb="25px" variant="subtitle1">
          General Info
        </Typography>
        <div className={styles['container__general-info__fields']}>
          <TextField disabled label="Company Name*" value={data.name} variant="outlined" />
          <TextField
            disabled
            label="Company Former Name"
            value={data.former_name}
            variant="outlined"
          />
          <TextField disabled label="Web*" type="url" value={data.website_url} variant="outlined" />
          <TextField disabled label="Pitchbook URL" value={data.pitchbook_url} variant="outlined" />
          <TextField disabled label="Year Founded*" value={data.year_founded} variant="outlined" />
          <Autocomplete
            disabled
            multiple
            options={EMPTY_OPTIONS}
            placeholder="Industries*"
            value={selectedIndustries}
          />
          <DateField disabled label="First Financing Date*" value={firstFinancingDate} />
          <Autocomplete
            disabled
            options={EMPTY_OPTIONS}
            placeholder="First Financing Deal Type*"
            value={selectedFirstFinancingDealType}
          />
          <TextField
            disabled
            label="Description"
            sx={COMPANY_BIO_TEXT_FIELD_SX}
            title={data.bio}
            value={data.bio}
            variant="outlined"
          />
          <Autocomplete
            disabled
            multiple
            options={EMPTY_OPTIONS}
            placeholder="Competitors*"
            sx={COMPETITORS_AUTOCOMPLETE_SX}
            value={selectedCompetitors}
          />
        </div>
      </div>
      <div className={styles.container__rounds}>
        {data.rounds.map((round, index) => (
          <Round
            className={
              index !== data.rounds.length - 1 ? styles.container__rounds__round : undefined
            }
            data={round}
            index={index}
            key={round.type}
          />
        ))}
      </div>
      <div className={styles.container__comments}>
        <Typography color="text.primary" fontWeight="700" mb="8px" ml="4px" variant="subtitle1">
          Comments
        </Typography>
        <TextField disabled multiline value={data.comment} variant="outlined" />
      </div>
    </div>
  );
};

export const GeneralData = memo(Component);
